import { useCallback, useEffect, useRef, useState } from 'react'
import { dmpConnectJS2Api } from '@/core/api/dmp-connect-js2'
import { createDMPConnectSocket } from '@/core/api/socket'
import { BaseParams, StartCardMonitoringParams, StartCpxCardMonitoringResponse, StartVitaleCardMonitoringResponse } from '@/core/types/dmp-connect'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectDMPConnectSessionId, setCpxCardMonitoringData, setVitaleCardMonitoringData } from '@/redux/reducers/dmp-connect.reducer'
import { skipToken } from '@reduxjs/toolkit/query'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'
import { isEqual } from 'lodash'

export const {
  useGetDcServiceVersionQuery,
  useRegisterDcParameterMutation,
  useIsDcParameterRegisteredQuery,
  useLazyIsDcParameterRegisteredQuery,
  useOpenSessionMutation,
  useSessionExistsQuery,
  useLazySessionExistsQuery,
  useGetPcscReadersQuery,
  useGetCpxHpInfosQuery,
  useLazyGetCpxHpInfosQuery,
  useSelectCPxCardMutation,
  useReadCPxCardQuery,
  useLazyReadCPxCardQuery,
  useSelectVitaleCardMutation,
  useReadVitaleCardQuery,
  useLazyReadVitaleCardQuery,
  useCreateTlsiConnectorMutation,
  useGetInsFromVitaleCardQuery,
  useLazyGetInsFromVitaleCardQuery,
  useGetInsFromIdentityInformationQuery,
  useLazyGetInsFromIdentityInformationQuery,
  useCheckInsIdentityQuery,
  useLazyCheckInsIdentityQuery,
  useSubmitInsiBatchValidationSetMutation,
  useGetInsiBatchValidationSetResultQuery,
  useLazyGetInsiBatchValidationSetResultQuery,
  useGetInteropCodesQuery,
} = dmpConnectJS2Api

function useDmpConnectSocket() {
  const dmpConnectSessionId = useAppSelector(selectDMPConnectSessionId)

  const { data: sessionExists } = useSessionExistsQuery(dmpConnectSessionId ?? skipToken)
  const isSessionValid = sessionExists?.i_exists === 1

  const [socket, setSocket] = useState<WebSocket>()
  const socketState = socket?.readyState

  useEffect(() => {
    if (!isSessionValid) return
    if (!socket || socketState === WebSocket.CLOSED) {
      socket?.close()
      createDMPConnectSocket().then(setSocket)
    }
  }, [isSessionValid, socket, socketState])

  return socket
}

type MonitoringCardType = 'cpx' | 'vitale'
type UseStartCardMonitoringReturn<T> = {
  isMonitored: boolean
  startMonitoring: (checkingInterval?: number) => void
  stopMonitoring: () => Promise<void>
  cpxCardMonitoringData: StartCpxCardMonitoringResponse | null
  vitaleCardMonitoringData: StartVitaleCardMonitoringResponse | null
}

export function useStartCardMonitoring<T extends MonitoringCardType>(cardType: T): UseStartCardMonitoringReturn<T> {
  const dispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const dmpConnectSessionId = useAppSelector(selectDMPConnectSessionId)
  const cpxCardMonitoringData = useAppSelector((state) => (cardType === 'cpx' ? state.dmpConnect.monitoring.cpxCard : null))
  const vitaleCardMonitoringData = useAppSelector((state) => (cardType === 'vitale' ? state.dmpConnect.monitoring.vitaleCard : null))

  const socket = useDmpConnectSocket()

  const [isMonitored, setIsMonitored] = useState(false)

  const startMonitoring = useCallback(
    (checkingInterval: number = 5) => {
      if (!dmpConnectSessionId || !socket) return
      if (socket.readyState !== WebSocket.OPEN) {
        enqueueSnackbar(t`An error has occurred while trying to monitor ${cardType} card`, { variant: 'error' })
        return
      }
      const cardTypeToCommandNameMap = {
        cpx: 'hl_startCpxCardMonitoring',
        vitale: 'hl_startVitaleCardMonitoring',
      }
      const params: BaseParams & StartCardMonitoringParams = {
        i_checkingInterval: checkingInterval,
        s_commandName: cardTypeToCommandNameMap[cardType],
        s_sessionId: dmpConnectSessionId,
      }
      console.log(JSON.stringify(params))
      socket.send(JSON.stringify(params))

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (isEqual(data, { s_status: 'OK' })) setIsMonitored(true)
        switch (cardType) {
          case 'cpx':
            const cardMonitoringData = data as StartCpxCardMonitoringResponse
            dispatch(setCpxCardMonitoringData(cardMonitoringData))
            break
          case 'vitale':
            const vitaleCardMonitoringData = data as StartVitaleCardMonitoringResponse
            if (vitaleCardMonitoringData.i_cardStatus) {
              dispatch(setVitaleCardMonitoringData(vitaleCardMonitoringData))
            }
            break
        }
      }
    },
    [cardType, dispatch, dmpConnectSessionId, socket]
  )

  const stopMonitoring = useCallback(() => {
    setIsMonitored(false)
    socket?.close()
    return new Promise<void>((resolve) => {
      if (socket) {
        socket.onclose = () => {
          resolve()
        }
      } else {
        resolve()
      }
    })
  }, [socket])

  useEffect(() => {
    return () => {
      stopMonitoring()
    }
  }, [stopMonitoring])

  return { isMonitored, startMonitoring, stopMonitoring, cpxCardMonitoringData, vitaleCardMonitoringData }
}
